/**
 * RelatedTasksListPanel Component
 *
 * Panel for selecting related tasks during form editing.
 * Uses SlidePanel for consistent desktop/mobile UX.
 *
 * Features:
 * - Desktop: Slide-in panel from right (resizable, width persisted)
 * - Mobile: Full-screen modal with swipe-down support
 * - Task selection with filters and search
 * - ESC key closes panel
 * - 100% CSS Modules (via SlidePanel)
 * - 100% Zustand state management (atomic selectors)
 *
 * Architecture:
 * - Wrapper component that combines RelatedTasksCore + SlidePanel
 * - Zero local state (100% Zustand)
 * - Minimal re-renders (atomic selectors)
 * - DRY principle (reuses SlidePanel)
 *
 * @module components/tasks/wizard/steps/Step4RelatedTasks/RelatedTasksListPanel
 */

import { memo, useCallback } from 'react';
import { Button, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useGeoMarkupStore } from '../../../../../../store';
import { SlidePanel } from '../../../../../ui/SlidePanel';
import { RelatedTasksCore } from './core';
import type { TaskListItem } from '../../../../../../types';

interface RelatedTasksListPanelProps {
  selectedTaskIds: string[];
  selectedTasks: TaskListItem[];
  onTasksSelect: (taskIds: string[]) => void;
  onCacheTasks: (items: TaskListItem[]) => void;
  editingTaskId?: string | null;
}

/**
 * RelatedTasksListPanel - Panel for selecting related tasks
 *
 * Combines RelatedTasksCore (logic) with SlidePanel (UI wrapper)
 * for consistent UX on desktop and mobile.
 */
export const RelatedTasksListPanel = memo(function RelatedTasksListPanel({
  selectedTaskIds,
  selectedTasks,
  onTasksSelect,
  onCacheTasks,
  editingTaskId,
}: RelatedTasksListPanelProps) {
  const { t } = useTranslation();

  // Zustand state - atomic selectors (minimal re-renders)
  const isOpen = useGeoMarkupStore((s) => s.ui.relatedTasksList.isOpen);
  const toggleRelatedTasksList = useGeoMarkupStore(
    (s) => s.ui.toggleRelatedTasksList
  );
  // Memoized close handler
  const handleClose = useCallback(() => {
    toggleRelatedTasksList(false);
  }, [toggleRelatedTasksList]);

  return (
    <SlidePanel
      opened={isOpen}
      onClose={handleClose}
      headerContent={<h2>{t('wizard.step4.relatedTasksList.title')}</h2>}
      footerContent={
        <Group justify="flex-end">
          <Button onClick={handleClose}>
            {t('wizard.step4.relatedTasksList.backToFormButton')}
          </Button>
        </Group>
      }
      width={480}
      withCloseButton={true}
      closeButtonAriaLabel={t('common.close')}
      allowMinimize={false}
      disableMobileAnimations={true}
    >
      <RelatedTasksCore
        selectedTaskIds={selectedTaskIds}
        selectedTasks={selectedTasks}
        onTasksSelect={onTasksSelect}
        onCacheTasks={onCacheTasks}
        editingTaskId={editingTaskId}
      />
    </SlidePanel>
  );
});
